import { useCallback, useRef, useReducer } from 'react';

type PopoverState<T> = {
  active: T | null;
  exiting: T | null;
};

type PopoverAction<T> =
  | { type: 'open'; payload: T }
  | { type: 'close' }
  | { type: 'clearExiting' };

function popoverReducer<T>(
  state: PopoverState<T>,
  action: PopoverAction<T>,
  getId: (item: T) => string
): PopoverState<T> {
  switch (action.type) {
    case 'open': {
      const next = action.payload;
      const nextId = getId(next);
      if (state.active && getId(state.active) === nextId) {
        return { active: null, exiting: state.active };
      }
      if (state.active && getId(state.active) !== nextId) {
        return { active: next, exiting: state.active };
      }
      return { active: next, exiting: null };
    }
    case 'close':
      return { active: null, exiting: state.active };
    case 'clearExiting':
      return { ...state, exiting: null };
    default:
      return state;
  }
}

export function useAnimatedPopover<T>(getId: (item: T) => string) {
  const getIdRef = useRef(getId);
  getIdRef.current = getId;

  const reducer = useCallback((state: PopoverState<T>, action: PopoverAction<T>) => {
    return popoverReducer(state, action, getIdRef.current);
  }, []);

  const [state, dispatch] = useReducer(reducer, { active: null, exiting: null });

  const open = useCallback((item: T) => {
    dispatch({ type: 'open', payload: item });
  }, []);

  const close = useCallback(() => {
    dispatch({ type: 'close' });
  }, []);

  const clearExiting = useCallback(() => {
    dispatch({ type: 'clearExiting' });
  }, []);

  return {
    active: state.active,
    exiting: state.exiting,
    open,
    close,
    clearExiting,
  };
}
